import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  QBox,
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSpinner,
  QStack,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import eventTypesApi from '../../api/eventTypes.api';
import templatesApi from '../../api/templates.api';
import { EventTag, EventTemplateField, ProductRootCause, QualityEvent, QualityEventCreate, User } from '../../types';
import EventForm from './components/EventForm';
import { DefaultErrorMessage } from '../../displayStrings';
import { ModalForm, ModalFormSchema, processFormValuesForSubmit } from './formHelpers';
import { useFlags } from '../../external/LaunchDarklyAdapter';
import { useEventMetadata } from '../../hooks/useEventMetadata';
import { useProducts } from '../../hooks/useProducts';
import { useRootCauses } from '../../hooks/useRootCauses';
import { linkedRegistryItemIds } from '../../utils/eventUtils';

type EscalateEventModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (payload: QualityEventCreate) => Promise<QualityEvent | void>;
  event: QualityEvent;
  selectsData: {
    eventTags: EventTag[];
    users: User[];
  };
};

const EscalateEventModal: React.FC<EscalateEventModalProps> = ({ isOpen, setIsOpen, onSave, event, selectsData }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { userId, companyId } = useCurrentUser();
  const { isLoading, data } = useQuery('eventTypesList', () => eventTypesApi.getAllEventTypes(companyId, 'effective'), {
    staleTime: 5 * 60 * 1000,
  });
  const [optionalFields, setOptionalFields] = useState<EventTemplateField[]>([]);
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const { data: eventsMetadata } = useEventMetadata();
  const { data: productsList } = useProducts(linkedRegistryItemIds(event.products));
  const { data: rootCausesList } = useRootCauses(linkedRegistryItemIds(event.rootcauses));

  const formMethods = useForm<ModalForm>({
    mode: 'onSubmit',
    resolver: zodResolver(ModalFormSchema),
    defaultValues: {
      title: '',
      workflow_id: '',
      owner_id: '',
      description: '',
      time_limit: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    watch,
  } = formMethods;

  const eventType = watch('workflow_id');

  useEffect(() => {
    if (eventType) {
      const getOptionalFields = async () => {
        const templateDetails = await templatesApi.getEventTemplateDetails(companyId, parseInt(eventType, 10));
        if (!templateDetails) {
          return;
        }
        setOptionalFields(templateDetails.fields);
      };
      getOptionalFields().catch(console.error);
    }
  }, [eventType, companyId]);

  /**
   * Normally against using an effect to watch state, but this is the react hook form official
   * guidance for resetting on submit:
   * https://react-hook-form.com/docs/useform/reset
   */
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const formHasErrors = !!Object.keys(errors).length;

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const createNewEvent = useCallback(
    async (data: ModalForm) => {
      const { collaborators, ...processedData } = processFormValuesForSubmit(data);

      try {
        const optionalFieldValues = optionalFields.reduce((values, field) => {
          switch (field.attribute_name) {
            case 'risk': {
              let modifiedValues = values;
              if (event.probability_id) {
                modifiedValues = { ...modifiedValues, probability_id: String(event.probability_id) };
              }
              if (event.severity_id) {
                modifiedValues = { ...modifiedValues, severity_id: String(event.severity_id) };
              }
              return modifiedValues;
            }
            case 'product':
              if (event.products && event.products.length > 0) {
                return {
                  ...values,
                  related_products: event.products?.map((product: ProductRootCause) => product.matrix_id),
                };
              }
              break;
            case 'root_cause':
              if (event.rootcauses && event.rootcauses.length > 0) {
                return {
                  ...values,
                  related_rootcauses: event.rootcauses?.map((rootcause: ProductRootCause) => rootcause.matrix_id),
                };
              }
              break;
            case 'supplier':
              return { ...values, suppliers: event.suppliers };
            default:
              break;
          }
          return values;
        }, {});
        const newEventPayload: QualityEventCreate = {
          ...processedData,
          ...optionalFieldValues,
          related_documents: event.related_documents,
          related_events: event.related_events,
          tags_ids: event.tags_ids.map(String),
          escalated_from: [event.id],
          ...(isEventCollaboratorsEnabled && { collaborators: collaborators ?? [] }),
        };
        const newEvent = await onSave(newEventPayload);
        setIsOpen(false);
        if (newEvent) {
          showToast({
            title: t('common.notifications.newEventCreated'),
            description: t('common.notifications.eventCreatedDesc', {
              eventCode: newEvent.code,
              eventTitle: newEvent.title,
            }),
            status: 'success',
          });
        }
      } catch {
        showToast({
          title: t('common.notifications.eventNotCreated'),
          description: DefaultErrorMessage,
          status: 'error',
        });
      }
    },
    [onSave, showToast, setIsOpen, event, optionalFields, isEventCollaboratorsEnabled, t],
  );
  const submitFn = handleSubmit(createNewEvent);

  return (
    <QModal isOpen={isOpen} onClose={() => null} size="xl">
      <QModalHeader>
        <QText>{t('events.actions.escalateAndCreateNewEvent')}</QText>
        <QCloseButton onClick={handleClose} data-metrics="escalate-event-close-modal-button"></QCloseButton>
      </QModalHeader>

      <QModalBody>
        <QStack direction="column" color="gray.700" spacing={4}>
          {isLoading ? (
            <QBox w="100%" textAlign="center">
              <QSpinner />
            </QBox>
          ) : (
            <EventForm
              formMethods={formMethods}
              onSubmit={submitFn}
              data={{
                ...selectsData,
                probabilities: eventsMetadata?.probabilities ?? [],
                severities: eventsMetadata?.severities ?? [],
                risks: eventsMetadata?.risks ?? [],
                products: productsList ?? [],
                rootCauses: rootCausesList ?? [],
                eventTypes: data ? data : [],
              }}
              event={event}
              userId={userId}
              optionalFields={optionalFields}
            />
          )}
        </QStack>
      </QModalBody>

      <QModalActions>
        <QButton onClick={handleClose} variant="outline">
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          onClick={submitFn}
          isDisabled={formHasErrors || isSubmitting}
          data-metrics="escalate-event-button"
          data-cy="escalate-event-button"
        >
          {t('events.actions.escalateAndCreateNewEvent')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default EscalateEventModal;
